import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  TableContainer
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import NavigationDrawer from '../Navigation/NavigationDrawer';
import authApi from '../../services/authApi';
import { etlAdminService } from '../../services/etlAdminService';

const STATUS_COLORS = {
  healthy: 'success',
  running: 'info',
  failed: 'error',
  stalled: 'warning',
  investigate: 'error',
  warning: 'warning',
  idle: 'default',
  disabled: 'default',
  not_found: 'default'
};

const REFRESH_MS = 60_000;

const stickyActionsCell = {
  position: 'sticky',
  right: 0,
  bgcolor: 'background.paper',
  zIndex: 1,
  whiteSpace: 'nowrap',
  boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.12)'
};

function SummaryCard({ label, value, color = 'text.primary' }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="h4" color={color}>{value ?? 0}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString();
  } catch (_) {
    return String(d);
  }
}

function formatDuration(sec) {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function EtlOperationsDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [actionMsg, setActionMsg] = useState('');
  const statusFilterRef = useRef(statusFilter);
  statusFilterRef.current = statusFilter;

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setInitialLoading(true);
    }
    setError('');
    try {
      const token = authApi.getToken();
      const data = await etlAdminService.getDashboard(token, {
        limit: 200,
        status: statusFilterRef.current || undefined
      });
      setOverview(data.overview);
      setRows(data.rows || []);
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.message || e.message || 'Failed to load ETL operations data');
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load({ silent: Boolean(overview) });
  }, [statusFilter, load]);

  useEffect(() => {
    const interval = setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  const openDetail = async (configId) => {
    try {
      const token = authApi.getToken();
      const [d, h] = await Promise.all([
        etlAdminService.getConfigDetail(token, configId),
        etlAdminService.getConfigHistory(token, configId, { limit: 20 })
      ]);
      setDetail(d);
      setHistory(h.rows || []);
    } catch (e) {
      setActionMsg(e.message);
    }
  };

  const runAction = async (fn, msg) => {
    setActionMsg('');
    try {
      const token = authApi.getToken();
      await fn(token);
      setActionMsg(msg);
      await load({ silent: true });
      if (detail) await openDetail(detail.configId);
    } catch (e) {
      setActionMsg(e.response?.data?.message || e.message);
    }
  };

  const grouped = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const k = r.customerEmail;
      if (!map[k]) map[k] = { customerEmail: k, customerName: r.customerName, sites: [] };
      map[k].sites.push(r);
    });
    return Object.values(map);
  }, [rows]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection=""
        setActiveSection={() => {}}
        navigate={navigate}
      />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
    <Container maxWidth="xl" sx={{ py: 0, px: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">ETL Operations</Typography>
        <Box>
          <Tooltip title="Re-read ETLDefinition rows from etl-inventory-input.json on the server (does not start/stop runners)">
            <Button
              size="small"
              startIcon={<AppRegistrationIcon />}
              onClick={() => runAction((t) => etlAdminService.seedInventory(t), 'Inventory seeded')}
              sx={{ mr: 1 }}
            >
              Seed inventory
            </Button>
          </Tooltip>
          <Tooltip title="Reload dashboard now (auto-refresh runs every 60 seconds)">
            <IconButton onClick={() => load({ silent: Boolean(overview) })} aria-label="Refresh" disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {refreshing && <LinearProgress sx={{ mb: 1 }} />}
      {lastUpdated && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Last updated {lastUpdated.toLocaleTimeString()} · auto-refresh every 60s
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {actionMsg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionMsg('')}>{actionMsg}</Alert>}

      {overview && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}><SummaryCard label="Customers" value={overview.totalCustomers} /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard label="Sites" value={overview.totalSites} /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard label="Running" value={overview.running} color="info.main" /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard label="Healthy" value={overview.healthy} color="success.main" /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard label="Failed" value={overview.failed} color="error.main" /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard label="Stalled" value={overview.stalled} color="warning.main" /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard label="Investigate" value={overview.investigate} color="error.main" /></Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="etl-status-filter">Status</InputLabel>
          <Select
            labelId="etl-status-filter"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="healthy">Healthy</MenuItem>
            <MenuItem value="running">Running</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="stalled">Stalled</MenuItem>
            <MenuItem value="investigate">Investigate</MenuItem>
            <MenuItem value="idle">Idle</MenuItem>
            <MenuItem value="not_found">Not scheduled</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {initialLoading && !rows.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        grouped.map((group) => (
          <Paper key={group.customerEmail} sx={{ mb: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.100' }}>
              <Typography variant="subtitle1">{group.customerName}</Typography>
              <Typography variant="caption" color="text.secondary">{group.customerEmail}</Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Site</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last run</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell align="right" sx={stickyActionsCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.sites.map((site) => (
                  <TableRow key={site.configId} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(site.configId)}>
                    <TableCell>{site.siteLabel}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={site.status === 'investigate' ? 'investigate' : site.status.replace('_', ' ')}
                        color={STATUS_COLORS[site.status] || 'default'}
                        variant={site.status === 'investigate' ? 'filled' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(site.lastRunAt)}</TableCell>
                    <TableCell>{formatDuration(site.lastDurationSeconds)}</TableCell>
                    <TableCell>{site.lastRecordsProcessed ?? '—'}</TableCell>
                    <TableCell align="right" sx={stickyActionsCell} onClick={(e) => e.stopPropagation()}>
                      {!site.isRegistered && (
                        <Tooltip title="Register for ETL">
                          <IconButton size="small" onClick={() => runAction((t) => etlAdminService.register(t, site.configId), 'Registered')}>
                            <AppRegistrationIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Run now">
                        <IconButton size="small" onClick={() => runAction((t) => etlAdminService.runNow(t, site.configId), 'Run queued')}>
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {site.isEnabled !== false ? (
                        <Tooltip title="Disable (pause — skip future loops)">
                          <IconButton size="small" onClick={() => runAction((t) => etlAdminService.disable(t, site.configId), 'Disabled')} aria-label="Disable site">
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Enable">
                          <IconButton size="small" onClick={() => runAction((t) => etlAdminService.enable(t, site.configId), 'Enabled')}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      <Drawer anchor="right" open={Boolean(detail)} onClose={() => setDetail(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
        {detail && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">{detail.siteLabel}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>{detail.customerEmail}</Typography>
            <Chip label={detail.status} color={STATUS_COLORS[detail.status] || 'default'} size="small" sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => runAction((t) => etlAdminService.runNow(t, detail.configId), 'Run queued')}
              >
                Run now
              </Button>
              {detail.isEnabled !== false ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<BlockIcon />}
                  onClick={() => runAction((t) => etlAdminService.disable(t, detail.configId), 'Disabled')}
                >
                  Disable (pause)
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => runAction((t) => etlAdminService.enable(t, detail.configId), 'Enabled')}
                >
                  Enable (resume)
                </Button>
              )}
            </Box>

            <Typography variant="subtitle2">Last error</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>{detail.lastErrorMessage || 'None'}</Typography>

            <Typography variant="subtitle2">Recent logs</Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
              {(detail.recentLogs || []).slice(0, 10).map((log) => (
                <Typography key={log.id} variant="caption" display="block" sx={{ mb: 0.5 }}>
                  [{log.log_level}] {formatDate(log.created_at)} — {log.message?.slice(0, 120)}
                </Typography>
              ))}
            </Box>

            <Typography variant="subtitle2">Execution history</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Started</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Dur</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.execution_id}>
                    <TableCell>{formatDate(h.started_at)}</TableCell>
                    <TableCell>{h.status}</TableCell>
                    <TableCell>{formatDuration(h.duration_seconds)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Drawer>
    </Container>
      </Box>
    </Box>
  );
}
